import { cloneDeep } from 'lodash';
import { i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import { PickerOption } from '../../../shared/component/widget/widget.types';
import { MoveToPointProps } from '../../../shared/hook/move-to-point.hooks';
import {
  FineTuneProps,
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemMetadata,
  FlowItemParameter,
  FlowItemParameterMetadata,
  FlowItemSharedRef,
  FlowItemText,
  FlowSelectedItemInfo,
  SystemUnit,
} from '../../../shared/model/flow.types';
import { AbstractFlowItemService } from '../../../shared/service/abstract-flow-item.service';
import {
  ConvertMotionPointToFlowItemParams,
  ConvertMotionPointsToFlowItemSectionParams,
  ConvertMotionPointsToFlowItemSectionsParams,
} from '../../../shared/service/service.types';
import {
  incrementItemStats,
  instanceOfHelper,
} from '../../../shared/util/flow-item.util';
import {
  convertParameterValueToUOM,
  getParameterValue,
} from '../../../shared/util/flow-parameter.util';
import { getNextChar } from '../../../shared/util/string';
import { BeaconPaintingData } from '../model/beacon-painting-data';

class PaintingFlowItemServiceImpl extends AbstractFlowItemService<
  BeaconPaintingData.FlowConfig,
  typeof BeaconPaintingData.FlowSectionType
> {
  constructor(items: FlowItemMetadata[]) {
    super(items, BeaconPaintingData.FlowItemRef.PAINT);
  }

  public canConvertFlowItemToMultipass(_item: FlowItem): boolean {
    // Not implemented for painting yet
    return false;
  }

  public canConvertFlowItemToStitch(_item: FlowItem): boolean {
    // Not implemented for painting yet
    return false;
  }

  public convertFlowItemsToMotionPoints(_items: FlowItem[]): any[] {
    throw new Error('Method not implemented.');
  }

  public isMultipassSupportedMotionType(_item: FlowItem): boolean {
    throw new Error('Method not implemented');
  }

  public isStitchSupportedMotionType(_item: FlowItem): boolean {
    throw new Error('Method not implemented');
  }

  public convertMotionPointsToFlowItemSections(
    _params: ConvertMotionPointsToFlowItemSectionsParams<
      typeof BeaconPaintingData.FlowSectionType
    >
  ): FlowItem[] {
    throw new Error('Method not implemented.');
  }

  public convertMotionPointsToFlowItemSection(
    _params: ConvertMotionPointsToFlowItemSectionParams<
      typeof BeaconPaintingData.FlowSectionType
    >
  ): FlowItem[] {
    throw new Error('Method not implemented.');
  }

  public convertMotionPointToFlowItem(
    _params: ConvertMotionPointToFlowItemParams
  ): FlowItem {
    throw new Error('Method not implemented.');
  }

  public getRequiredParametersForTeaching(
    ref: string
  ): FlowItemParameterMetadata[] {
    const item = this.getItemMetadata(ref);
    const params: FlowItemParameterMetadata[] = [];
    if (ref === BeaconPaintingData.FlowItemRef.PAINT) {
      const type = item?.availableParameters.find((p) => p.apiName === 'type');
      if (type) {
        params.push(type);
      }
    }
    return params;
  }

  public recalculateParameterValues(
    config: BeaconPaintingData.FlowConfig,
    items: FlowItem[],
    options?: {
      isDefaultParameterSet?: boolean;
    }
  ): FlowItem[] {
    const { isDefaultParameterSet = false } = options ?? {};
    let contiguousPaints = 0;
    let itemIdx = 0;

    for (const item of items) {
      const itemMetadata = this.getItemMetadata(item.ref);

      if (item.ref !== BeaconPaintingData.FlowItemRef.SECTION && itemMetadata) {
        const singleItemPaint = this.isSinglePrimaryActionItem(item);

        if (
          item.ref === BeaconPaintingData.FlowItemRef.PAINT &&
          !singleItemPaint
        ) {
          contiguousPaints++;
        } else {
          contiguousPaints = 0;
        }

        for (const parameter of item.parameters) {
          const parameterMetadata = this.getParameterMetadata(
            item.ref,
            parameter.ref
          );

          if (parameterMetadata?.calculateValue) {
            parameter.value = parameterMetadata.calculateValue(
              config,
              item,
              parameter.value,
              items
            );
          }
        }

        // if the parameter set is the default parameter set it is neither an
        // approach nor a depart
        if (isDefaultParameterSet) {
          this.setParameterValue(
            item,
            BeaconPaintingData.FlowItemParameterRef.APPROACH,
            false
          );
          this.setParameterValue(
            item,
            BeaconPaintingData.FlowItemParameterRef.DEPART,
            false
          );
        } else if (singleItemPaint) {
          this.setParameterValue(
            item,
            BeaconPaintingData.FlowItemParameterRef.APPROACH,
            true
          );
          this.setParameterValue(
            item,
            BeaconPaintingData.FlowItemParameterRef.DEPART,
            true
          );
        } else {
          this.setParameterValue(
            item,
            BeaconPaintingData.FlowItemParameterRef.APPROACH,
            contiguousPaints === 1
          );

          if (contiguousPaints >= 1) {
            // we need to find the next item that is not a section
            let nextItem: FlowItem | undefined;

            for (let i = itemIdx + 1; i < items.length; i++) {
              nextItem = items[i];

              if (nextItem?.ref !== BeaconPaintingData.FlowItemRef.SECTION) {
                break;
              }
            }

            const isNextSingleItemPaint = nextItem
              ? this.isSinglePrimaryActionItem(nextItem)
              : false;
            if (
              nextItem === undefined ||
              nextItem?.ref !== BeaconPaintingData.FlowItemRef.PAINT ||
              isNextSingleItemPaint
            ) {
              this.setParameterValue(
                item,
                BeaconPaintingData.FlowItemParameterRef.DEPART,
                true
              );
            } else {
              this.setParameterValue(
                item,
                BeaconPaintingData.FlowItemParameterRef.DEPART,
                false
              );
            }
          } else {
            this.setParameterValue(
              item,
              BeaconPaintingData.FlowItemParameterRef.DEPART,
              false
            );
          }
        }
      }

      itemIdx++;
    }

    return items;
  }

  public getFineTuneProps(
    flow: Flow<BeaconPaintingData.FlowConfig>,
    item: FlowItem
  ): FineTuneProps {
    let supported = false;

    if (item.ref === BeaconPaintingData.FlowItemRef.PAINT) {
      supported = true;
    }

    const contiguousItems = this.getContiguousItemsForFlowItem(
      item,
      flow.items
    );

    const itemIndex = contiguousItems.findIndex((i) => i.id === item.id);
    const executingIndex = flow.items.findIndex((i) => i.id === item?.id);

    const isLast = itemIndex === contiguousItems.length - 1;

    const startItem: FlowItem = item;
    const endItem: FlowItem | undefined = isLast
      ? contiguousItems[itemIndex - 1]
      : contiguousItems[itemIndex + 1];

    const workPiecePosition =
      item.attributes?.workSurfacePosition ??
      this.getInferredWorkSurfacePosition({
        item,
        flowItems: flow.items ?? [],
      });

    const workPieceOffset = item.attributes?.workSurfaceOffset ?? 0;

    const fineTuneProps: FineTuneProps = {
      supported,
      type: 'linear',
      pointToProcess: 0, // FineTunedPointRequired.START_POINT
      startPoint: getParameterValue(
        startItem,
        BeaconPaintingData.FlowItemParameterRef.WAYPOINT
      ),
      endPoint: endItem
        ? getParameterValue(
            endItem,
            BeaconPaintingData.FlowItemParameterRef.WAYPOINT
          )
        : undefined,
      toolConfig: flow.config.applicatorTool,
      stickout: 0,
      poseIncludesStickout: true,
      workPiecePosition,
      workPieceOffset,
      ui: {
        path: {
          label: 'painting:paint_path',
        },
        stickout: {
          label: 'painting:stickout_offset',
        },
        travelAngle: {
          label: 'painting:travel_angle',
        },
        workAngle: {
          label: 'painting:work_angle',
        },
        applyTo: {
          label: 'painting:fine_tune_apply_to',
        },
      },
      executingIndex: executingIndex,
    };

    return fineTuneProps;
  }

  public getMoveToPointProps(
    flow: Flow<BeaconPaintingData.FlowConfig>,
    item?: FlowItem
  ): MoveToPointProps {
    let moveToPointProps: MoveToPointProps = {};

    const isMoveHome = item?.ref === BeaconPaintingData.FlowItemRef.MOVE_HOME;

    let point: Waypoint | undefined = undefined;
    if (item) {
      point = isMoveHome
        ? flow.config.home.waypoint
        : getParameterValue<Waypoint>(
            item,
            BeaconPaintingData.FlowItemParameterRef.WAYPOINT
          );
    }

    if (point && item) {
      const qnear = isMoveHome
        ? flow.config.home.qnear
        : getParameterValue<QNear>(
            item,
            BeaconPaintingData.FlowItemParameterRef.QNEAR
          );

      const itemIndex = flow.items.findIndex((i) => i.id === item?.id);

      moveToPointProps = {
        point,
        qnear,
        stickout: 0,
        toolConfig: flow.config.applicatorTool,
        executingIndex: itemIndex,
      };
    }

    return moveToPointProps;
  }

  public buildFlowItemTextArr(
    flowItems: FlowItemArrayType[]
  ): (FlowItemText | undefined)[] {
    let currentSectionIdx = 0;
    let currentItemIdx = 1;
    let currentAlpha = 'a';
    let prevItem: FlowItem | undefined;

    return flowItems.map<FlowItemText | undefined>((item) => {
      if (instanceOfHelper(item) || !item.id) {
        return undefined;
      }

      let filteredName = item.name?.replace(/ - \d+$/, '') ?? '';

      if (item.ref === BeaconPaintingData.FlowItemRef.SECTION) {
        currentSectionIdx++;

        return {
          itemId: item.id,
          index: `${currentSectionIdx}`,
          name: filteredName,
          label: `${currentSectionIdx} - ${filteredName}`,
        };
      }

      if (item.ref === BeaconPaintingData.FlowItemRef.WAIT_FOR) {
        const type = getParameterValue(
          item,
          BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
        );

        if (type === BeaconPaintingData.WaitForInputType.OPERATOR_BUTTON) {
          const message = getParameterValue<string>(
            item,
            BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          const defaultItem = this.getDefaultItemForRef(
            BeaconPaintingData.FlowItemRef.WAIT_FOR
          );
          const defaultName = this.generateFlowItemName(
            BeaconPaintingData.FlowItemRef.WAIT_FOR
          );
          const defaultMessage = getParameterValue<string>(
            defaultItem,
            BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE
          );
          if (filteredName === defaultName) {
            if (message?.length > 0 && message !== defaultMessage) {
              filteredName = message;
            }
          }
        }
      }

      const isFirst = prevItem === undefined;
      const singleItemPaint = this.isSinglePrimaryActionItem(item);

      if (
        item.ref !== BeaconPaintingData.FlowItemRef.PAINT ||
        singleItemPaint
      ) {
        // this logic handles non-paints and single point paints
        if (!isFirst) {
          ++currentItemIdx;
        }

        prevItem = item;

        return {
          itemId: item.id,
          index: `${currentItemIdx}`,
          name: filteredName,
          label: `${currentItemIdx} - ${filteredName}`,
        };
      } else {
        // this logic handles contiguous paints
        if (isFirst) {
          prevItem = item;

          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        } else {
          // the only non-sequence breaking item is a contiguous paints
          // at this point, prevItem should always be set
          const prevIsNotSequenceBreaking =
            prevItem?.ref === BeaconPaintingData.FlowItemRef.PAINT &&
            !this.isSinglePrimaryActionItem(prevItem);

          if (prevIsNotSequenceBreaking) {
            currentAlpha = getNextChar(currentAlpha);
          } else {
            currentAlpha = 'a';
            ++currentItemIdx;
          }

          prevItem = item;
          return {
            itemId: item.id,
            index: `${currentItemIdx}${currentAlpha}`,
            name: filteredName,
            label: `${currentItemIdx}${currentAlpha} - ${filteredName}`,
          };
        }
      }
    });
  }

  public isShiftableItem(item: FlowItem): boolean {
    if (
      item.ref === BeaconPaintingData.FlowItemRef.MOVE ||
      item.ref === BeaconPaintingData.FlowItemRef.PAINT
    ) {
      return true;
    }

    return false;
  }

  public getFlowSelectedItemInfo(
    flow: Flow,
    selectedItems: string[]
  ): FlowSelectedItemInfo {
    const stats: FlowSelectedItemInfo = {
      linear: { count: 0, selected: 0 },
      radial: { count: 0, selected: 0 },
      other: { count: 0, selected: 0 },
    };

    for (let index = 0; index < flow?.items?.length; index++) {
      const item = flow.items[index];

      if (item.id !== undefined) {
        if (item.ref === BeaconPaintingData.FlowItemRef.PAINT) {
          const paintType = getParameterValue<BeaconPaintingData.PaintTypeEnum>(
            item,
            BeaconPaintingData.FlowItemParameterRef.PAINT_TYPE
          );

          if (paintType === BeaconPaintingData.PaintTypeEnum.LINEAR) {
            stats.linear = incrementItemStats(
              stats.linear,
              selectedItems.includes(item.id)
            );
          } else if (paintType === BeaconPaintingData.PaintTypeEnum.RADIAL) {
            stats.radial = incrementItemStats(
              stats.radial,
              selectedItems.includes(item.id)
            );
          }
        } else {
          stats.other = incrementItemStats(
            stats.other,
            selectedItems.includes(item.id)
          );
        }
      }
    }

    return stats;
  }

  public getFlowItemSubheading(
    flowItem: FlowItem<BeaconPaintingData.FlowItemRef>,
    system?: SystemUnit
  ): string | undefined {
    let subheading: string | undefined;

    const metadata = this.getItemMetadata(flowItem.ref);

    if (metadata) {
      if (flowItem.ref === BeaconPaintingData.FlowItemRef.PAINT) {
        subheading = this.getPaintItemSubheading(flowItem, system);
      } else if (
        flowItem.ref === BeaconPaintingData.FlowItemRef.MOVE ||
        flowItem.ref === BeaconPaintingData.FlowItemRef.MOVE_HOME
      ) {
        subheading = getLocalizedText(metadata.name);
      } else if (flowItem.ref === BeaconPaintingData.FlowItemRef.WAIT_FOR) {
        subheading = this.getWaitForItemSubheading(flowItem);
      } else if (
        flowItem.ref === BeaconPaintingData.FlowItemRef.PRODUCTION_REPORTING
      ) {
        subheading = this.getProductionItemSubheading(flowItem);
      } else if (
        flowItem.ref === BeaconPaintingData.FlowItemRef.TRIGGER_OUTPUT
      ) {
        subheading = this.getTriggerOutputItemSubheading(flowItem);
      }
    }

    return subheading;
  }

  public getApplicationDefaultParameterSetImplicitTags(): Record<
    string,
    unknown
  > {
    // TODO
    return {};
  }

  public getContiguousItemsForFlowItem(
    item: FlowItem,
    items: FlowItem[]
  ): FlowItem[] {
    if (item.ref === BeaconPaintingData.FlowItemRef.PAINT) {
      const start = this.getStartOfSequenceIndexFromFlowItem(item, items);
      const end = this.getEndOfSequenceIndexFromFlowItem(item, items);
      if (start !== undefined && end !== undefined) {
        return items.slice(start, end + 1);
      }
    }

    return [];
  }

  public getStartOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let startIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;

    const isPaintItem = flowItem.ref === BeaconPaintingData.FlowItemRef.PAINT;

    if (flowItemIndex !== undefined && isPaintItem) {
      if (this.hasApproach(flowItem)) {
        startIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i >= 0; i--) {
          const item = flowItems[i];
          if (
            !this.isOfType(FlowItemSharedRef.SECTION, item) &&
            isPaintItem &&
            this.hasApproach(item)
          ) {
            startIndex = i;
            break;
          }
        }
      }
    }
    return startIndex;
  }

  public getEndOfSequenceIndexFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): number | undefined {
    let endIndex: number | undefined = undefined;
    const flowItemIndex: number | undefined = flowItem.id
      ? flowItems.findIndex((i) => i.id !== undefined && i.id === flowItem.id)
      : undefined;

    const isPaintItem = flowItem.ref === BeaconPaintingData.FlowItemRef.PAINT;

    if (flowItemIndex !== undefined && isPaintItem) {
      if (this.hasDepart(flowItem)) {
        endIndex = flowItemIndex;
      } else {
        for (let i = flowItemIndex; i <= flowItems.length; i++) {
          const item = flowItems[i];
          if (
            !this.isOfType(FlowItemSharedRef.SECTION, item) &&
            isPaintItem &&
            this.hasDepart(item)
          ) {
            endIndex = i;
            break;
          }
        }
      }
    }
    return endIndex;
  }

  public getEndOfSequenceItemFromFlowItem(
    flowItem: FlowItem,
    flowItems: FlowItem[]
  ): FlowItem | undefined {
    const endIndex = this.getEndOfSequenceIndexFromFlowItem(
      flowItem,
      flowItems
    );
    return endIndex !== undefined ? flowItems[endIndex] : undefined;
  }

  public isSinglePrimaryActionItem(item: FlowItem): boolean {
    // Currently no single-item paint types
    return false;
  }

  public getCopyWithNestedItems(items: FlowItem[]): FlowItem[] {
    // Painting service doesn't use nested items, so just return a deep clone
    return cloneDeep(items);
  }

  protected getProductionItemSubheading(flowItem: FlowItem): string {
    const qty = getParameterValue(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.PRODUCTION_QUANTITY
    );

    const subheading = getLocalizedText(
      BeaconPaintingData.BeaconReportProductionItem.name
    ) as string;

    return `${subheading} (${getLocalizedText('common:quantity_abbr')} ${qty})`;
  }

  protected hasApproach(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconPaintingData.FlowItemParameterRef.APPROACH
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected hasDepart(item: FlowItem): boolean {
    const parameter: FlowItemParameter<boolean> | undefined =
      item.parameters.find(
        (p) => p.ref === BeaconPaintingData.FlowItemParameterRef.DEPART
      );
    if (parameter) {
      return parameter.value;
    }

    return false;
  }

  protected getPaintItemSubheading(
    flowItem: FlowItem,
    system?: SystemUnit
  ): string {
    const subheadingParts: string[] = [];

    const isApproach = this.hasApproach(flowItem);
    const isDepart = this.hasDepart(flowItem);
    const isSingleItemPaint = this.isSinglePrimaryActionItem(flowItem);

    if (isDepart && !isSingleItemPaint) {
      return `${getLocalizedText('painting:end_of_paint')}`;
    }

    const paintTypeText = this.getFlowItemPickerLabel(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.PAINT_TYPE
    );
    if (paintTypeText) {
      subheadingParts.push(paintTypeText);
    }

    const travel = getParameterValue<number>(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.TRAVEL_SPEED
    );

    if (travel) {
      let travelSpeedText: string;
      if (system) {
        const convertedTravelSpeed = convertParameterValueToUOM(
          BeaconPaintingData.TravelSpeedMetadata.value,
          system,
          Number(travel),
          false
        );
        travelSpeedText = `${convertedTravelSpeed} ${
          system === SystemUnit.IMPERIAL ? 'ipm' : 'cpm'
        }`;
      } else {
        travelSpeedText = `${travel}`;
      }

      subheadingParts.push(travelSpeedText);
    }

    return subheadingParts.join(', ');
  }

  protected getWaitForItemSubheading(flowItem: FlowItem): string {
    const waitForLabel = getLocalizedText(
      BeaconPaintingData.BeaconWaitForItem.name
    ) as string;

    const type = getParameterValue(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
    );

    const typeOption: PickerOption | undefined =
      BeaconPaintingData.WaitForTypeMetadata.ui?.widgetOptions.find(
        (o: PickerOption) => {
          return o.value === type;
        }
      );

    const typeLabel = getLocalizedText(
      typeOption?.label as i18nKeyType
    )?.toLowerCase();

    if (type === BeaconPaintingData.WaitForInputType.SENSOR) {
      const inputLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_INPUT
      )?.toLowerCase();

      const stateLabel = this.getFlowItemPickerLabel(
        flowItem,
        BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_INPUT_STATE
      )?.toLowerCase();

      return `${waitForLabel} ${inputLabel} - ${stateLabel}`;
    } else if (type === BeaconPaintingData.WaitForInputType.TIME) {
      const time = getParameterValue(
        flowItem,
        BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_TIME
      );
      return `${waitForLabel} ${time}s`;
    } else if (
      type === BeaconPaintingData.WaitForInputType.OPERATOR_BUTTON ||
      typeLabel === undefined
    ) {
      let suffix = typeLabel;

      // backwards compatibility for wait for operator label where we don't have a type
      if (typeLabel === undefined) {
        const option = this.getFlowItemPickerOptionByValue(
          flowItem,
          BeaconPaintingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
          BeaconPaintingData.WaitForInputType.OPERATOR_BUTTON
        );

        if (option) {
          suffix = getLocalizedText(option.label as i18nKeyType)?.toLowerCase();
        }
      }

      return `${waitForLabel} ${suffix}`;
    }

    return waitForLabel;
  }

  protected getTriggerOutputItemSubheading(flowItem: FlowItem): string {
    const triggerText = getLocalizedText('common:trigger');
    const outputText = getLocalizedText('common:output')?.toLowerCase();

    const outputLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.TRIGGER_OUTPUT_PORT
    )?.toLowerCase();

    const stateLabel = this.getFlowItemPickerLabel(
      flowItem,
      BeaconPaintingData.FlowItemParameterRef.TRIGGER_OUTPUT_STATE
    )?.toLowerCase();

    return `${triggerText} ${outputLabel} ${outputText} - ${stateLabel}`;
  }
}

export { PaintingFlowItemServiceImpl };
